import axios from '@/utils/axios';
import { BookingSession } from '../types';
import {
  attachAssignmentWarnings,
  type AssignmentWarning,
  type WithAssignmentWarnings,
} from '../utils/assignmentWarnings';

interface GetFilteredBookingSessionsParams {
  sessionFilter: string; // 'previous' | 'today' | 'upcoming' | 'unassigned' | 'cancelled'
  page: number;
  limit: number;
  search?: string;
  serviceId?: number;
  serviceTypeId?: number;
  startDate?: string | null;
  endDate?: string | null;
  statusId?: number;
  statusValue?: string;
  paymentStatusId?: number;
  paymentStatusValue?: string;
  order?: string;
  orderBy?: string;
  completedLast?: boolean;
}

interface GetFilteredBookingSessionsResponse {
  data: BookingSession[];
  total: number;
}

type GetAllFilteredBookingSessionsParams = Omit<
  GetFilteredBookingSessionsParams,
  'page' | 'limit'
> & {
  limit?: number;
};

const SESSION_ID_LIKE_FALLBACK_LIMIT = 150;

const isNumericSearch = (value?: string) =>
  typeof value === 'string' && /^\d+$/.test(value.trim());

const buildFilteredBookingSessionsParams = ({
  page,
  limit,
  search,
  serviceId,
  serviceTypeId,
  startDate,
  endDate,
  statusId,
  statusValue,
  paymentStatusId,
  paymentStatusValue,
  order,
  orderBy,
  completedLast,
}: Omit<GetFilteredBookingSessionsParams, 'sessionFilter'>) => {
  const params: Record<string, unknown> = {
    page,
    limit,
  };

  if (search) params.search = search;
  if (serviceId && serviceId !== 0) params.serviceId = serviceId;
  if (serviceTypeId && serviceTypeId !== 0)
    params.serviceTypeId = serviceTypeId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (statusId && statusId !== 0) {
    params.statusId = statusId;
    params.status_id = statusId;
  }
  if (statusValue) params.status = statusValue;
  if (paymentStatusId && paymentStatusId !== 0) {
    params.paymentStatusId = paymentStatusId;
    params.payment_status_id = paymentStatusId;
  }
  if (paymentStatusValue) params.paymentStatus = paymentStatusValue;
  if (order) {
    params.order = order;
    params.orderDir = order;
  }
  if (orderBy) params.orderBy = orderBy;
  if (completedLast) params.completedLast = true;

  return params;
};

const parseDay = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getSessionStatusKey = (session: BookingSession) =>
  (
    session.booking_session_status?.value ??
    session.booking_session_status?.display ??
    ''
  ).toLowerCase();

const getSessionPaymentStatusKey = (session: BookingSession) =>
  (
    session.booking_session_payment_status?.value ??
    session.booking_session_payment_status?.display ??
    ''
  ).toLowerCase();

const sessionMatchesTabFilter = (
  session: BookingSession,
  sessionFilter: string
) => {
  const normalizedFilter = sessionFilter.toLowerCase();
  const todayDateString = getTodayDateString();
  const sessionDateString = session.date?.slice(0, 10);
  const sessionStatus = getSessionStatusKey(session);

  switch (normalizedFilter) {
    case 'previous':
      return !!sessionDateString && sessionDateString < todayDateString;
    case 'today':
      return sessionDateString === todayDateString;
    case 'upcoming':
      return !!sessionDateString && sessionDateString > todayDateString;
    case 'unassigned':
      return (session.booking_session_cleaners ?? []).length === 0;
    case 'cancelled':
      return sessionStatus === 'cancelled' || sessionStatus === 'canceled';
    default:
      return true;
  }
};

const sessionMatchesActiveFilters = (
  session: BookingSession,
  {
    sessionFilter,
    serviceId,
    serviceTypeId,
    startDate,
    endDate,
    statusId,
    statusValue,
    paymentStatusId,
    paymentStatusValue,
  }: Pick<
    GetFilteredBookingSessionsParams,
    | 'sessionFilter'
    | 'serviceId'
    | 'serviceTypeId'
    | 'startDate'
    | 'endDate'
    | 'statusId'
    | 'statusValue'
    | 'paymentStatusId'
    | 'paymentStatusValue'
  >
) => {
  if (!sessionMatchesTabFilter(session, sessionFilter)) return false;

  if (serviceTypeId && serviceTypeId !== 0) {
    if (session.service_variant?.id !== serviceTypeId) return false;
  }

  if (serviceId && serviceId !== 0) {
    const sessionServiceId = session.service_variant?.service?.id;
    if (sessionServiceId !== serviceId) return false;
  }

  if (statusId && statusId !== 0) {
    if (session.status_id !== statusId) return false;
  }

  if (statusValue) {
    const expectedStatus = statusValue.toLowerCase();
    const sessionStatus = getSessionStatusKey(session);
    const sessionStatusDisplay =
      session.booking_session_status?.display?.toLowerCase();

    if (
      sessionStatus !== expectedStatus &&
      sessionStatusDisplay !== expectedStatus
    ) {
      return false;
    }
  }

  if (paymentStatusId && paymentStatusId !== 0) {
    if (session.payment_status_id !== paymentStatusId) return false;
  }

  if (paymentStatusValue) {
    const expectedPaymentStatus = paymentStatusValue.toLowerCase();
    const sessionPaymentStatus = getSessionPaymentStatusKey(session);
    const sessionPaymentStatusDisplay =
      session.booking_session_payment_status?.display?.toLowerCase();

    if (
      sessionPaymentStatus !== expectedPaymentStatus &&
      sessionPaymentStatusDisplay !== expectedPaymentStatus
    ) {
      return false;
    }
  }

  if (startDate || endDate) {
    const sessionDate = parseDay(session.date);

    if (!sessionDate) return false;
    if (startDate && sessionDate < new Date(startDate)) return false;
    if (endDate && sessionDate > new Date(endDate)) return false;
  }

  return true;
};

const getSessionsByLikeNumericSearch = async (
  params: GetFilteredBookingSessionsParams,
  numericSearch: string
): Promise<GetFilteredBookingSessionsResponse> => {
  const matchingSessions: BookingSession[] = [];
  const fallbackLimit = Math.max(params.limit, SESSION_ID_LIKE_FALLBACK_LIMIT);
  let fallbackPage = 1;
  let totalFallbackSessions = 0;

  do {
    const response = await axios.get(
      `/bookings/sessions/filter/${params.sessionFilter}`,
      {
        params: buildFilteredBookingSessionsParams({
          ...params,
          page: fallbackPage,
          limit: fallbackLimit,
          search: undefined,
        }),
      }
    );

    const {
      data: { booking_sessions, total },
    } = response;
    const sessions = (booking_sessions ?? []) as BookingSession[];

    totalFallbackSessions = total ?? sessions.length;
    matchingSessions.push(
      ...sessions.filter(
        (session) =>
          session.id.toString().includes(numericSearch) &&
          sessionMatchesActiveFilters(session, params)
      )
    );
    fallbackPage += 1;
  } while ((fallbackPage - 1) * fallbackLimit < totalFallbackSessions);

  const startIndex = (params.page - 1) * params.limit;

  return {
    data: matchingSessions.slice(startIndex, startIndex + params.limit),
    total: matchingSessions.length,
  };
};

const getSessionsByNumericSearch = async (
  params: GetFilteredBookingSessionsParams
): Promise<GetFilteredBookingSessionsResponse | null> => {
  if (!isNumericSearch(params.search)) return null;

  const numericSearch = params.search?.trim() ?? '';

  try {
    const session = await getBookingSession(Number(numericSearch));

    if (sessionMatchesActiveFilters(session, params)) {
      return {
        data: params.page === 1 ? [session] : [],
        total: 1,
      };
    }
  } catch (_error) {
    // Keep going: partial ID matches cannot be served by the exact session endpoint.
  }

  return getSessionsByLikeNumericSearch(params, numericSearch);
};

export async function getFilteredBookingSessionsPaginate({
  sessionFilter,
  page,
  limit,
  search,
  serviceId,
  serviceTypeId,
  startDate,
  endDate,
  statusId,
  statusValue,
  paymentStatusId,
  paymentStatusValue,
  order,
  orderBy,
  completedLast,
}: GetFilteredBookingSessionsParams): Promise<GetFilteredBookingSessionsResponse> {
  const getFilteredBookingSessionsParams = {
    sessionFilter,
    page,
    limit,
    search,
    serviceId,
    serviceTypeId,
    startDate,
    endDate,
    statusId,
    statusValue,
    paymentStatusId,
    paymentStatusValue,
    order,
    orderBy,
    completedLast,
  };
  const params = buildFilteredBookingSessionsParams(
    getFilteredBookingSessionsParams
  );

  // This endpoint should now support pagination and filters via query params
  const response = await axios.get(
    `/bookings/sessions/filter/${sessionFilter}`,
    {
      params,
    }
  );

  const {
    data: { booking_sessions, total },
  } = response;

  if (booking_sessions.length === 0 && total === 0) {
    const sessionIdSearchResult = await getSessionsByNumericSearch(
      getFilteredBookingSessionsParams
    );

    if (sessionIdSearchResult) return sessionIdSearchResult;
  }

  // The expected response format is { data: BookingSession[], total: number }
  return { data: booking_sessions, total };
}

export async function getAllFilteredBookingSessions({
  limit = 150,
  ...params
}: GetAllFilteredBookingSessionsParams): Promise<GetFilteredBookingSessionsResponse> {
  const pageSize = Math.max(limit, 1);
  const sessions: BookingSession[] = [];
  let page = 1;
  let total = 0;
  let lastPageCount = 0;

  do {
    const { data, total: responseTotal } =
      await getFilteredBookingSessionsPaginate({
        ...params,
        page,
        limit: pageSize,
      });

    if (page === 1) {
      total = responseTotal;
    }

    sessions.push(...data);
    lastPageCount = data.length;
    page += 1;
  } while (sessions.length < total && lastPageCount > 0);

  return { data: sessions, total };
}

export async function getSchedulerBookingSessions(
  parameter?: string
): Promise<BookingSession[]> {
  const {
    data: { bookings },
  } = await axios.get(`/bookings/sessions${parameter ?? ''}`);
  return bookings;
}

export async function getFilteredBookingSessions(
  sessionFilter: string, // 'previous' | 'today' | 'upcoming' | 'unassigned'
  parameter?: string
): Promise<BookingSession[]> {
  const { data } = await axios.get(
    `/bookings/sessions/filter/${sessionFilter}${parameter ?? ''}`
  );

  const bookingSessions = (data?.booking_sessions ?? []) as BookingSession[];

  return bookingSessions;
}

export async function getBookingSession(
  sessionId: number
): Promise<BookingSession> {
  const {
    data: { data },
  } = await axios.get(`/bookings/sessions/${sessionId}`);
  return data;
}

export async function getFilteredBookingSession(
  bookingId: number,
  sessionFilter: string, // 'completed' | 'today' | 'upcoming | 'unassigned',
  parameter?: string
): Promise<BookingSession[]> {
  const {
    data: { data },
  } = await axios.get(
    `/bookings/${bookingId}/sessions/filter/${sessionFilter}${parameter ?? ''}`
  );
  return data;
}

export async function updateBookingSession(
  bookingSessionId: number,
  bookingSessionDto: Partial<BookingSession>
): Promise<WithAssignmentWarnings<BookingSession>> {
  const response = await axios.put(
    `/bookings/sessions/${bookingSessionId}`,
    bookingSessionDto
  );

  const payload = response?.data;

  return attachAssignmentWarnings(payload?.data, payload?.warnings);
}

export async function acceptBookingSessionCleaner(
  bookingSessionId: number,
  cleanerId: number
): Promise<BookingSession> {
  const {
    data: { data },
  } = await axios.post(
    `/bookings/sessions/${bookingSessionId}/cleaners/${cleanerId}/accept`
  );

  return data;
}

export type BulkUpdateBookingSessionsFailedSession = {
  id: number;
  message: string;
};

export type BulkUpdateBookingSessionsResponse = {
  updated_sessions: BookingSession[];
  failed_sessions: BulkUpdateBookingSessionsFailedSession[];
  warnings?: AssignmentWarning[];
};

export async function updateBookingSessionsBulk(
  bookingSessionIds: number[],
  bookingSessionDto: Partial<BookingSession>
): Promise<BulkUpdateBookingSessionsResponse> {
  const response = await axios.put('/bookings/sessions/bulk', {
    session_ids: bookingSessionIds,
    data: bookingSessionDto,
  });

  const payload = response?.data;

  return attachAssignmentWarnings(payload?.data, payload?.warnings);
}

export async function bulkUnassignBookingSessions(
  bookingSessionIds: number[]
): Promise<BulkUpdateBookingSessionsResponse> {
  const response = await axios.put('/bookings/sessions/bulk', {
    session_ids: bookingSessionIds,
    data: { booking_cleaners: [] },
  });

  const payload = response?.data;

  return attachAssignmentWarnings(payload?.data, payload?.warnings);
}

export async function deleteBookingSession(
  bookingSessionId: number
): Promise<void> {
  await axios.delete(`/bookings/sessions/${bookingSessionId}`);
}

export async function deleteBookingCleanerSessionStatusHistory(
  statusHistoryId: number
): Promise<void> {
  await axios.delete(
    `/bookings/sessions/cleaner-status-history/${statusHistoryId}`
  );
}

export async function cloneBookingSession(
  bookingSessionId: number,
  bookingSessionDto: Partial<BookingSession>
): Promise<BookingSession> {
  const {
    data: { data },
  } = await axios.post(
    `/bookings/sessions/${bookingSessionId}/clone`,
    bookingSessionDto
  );

  return data[0];
}

export async function chargeBookingSessionBalance(
  bookingSessionId: number
): Promise<any> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/charge`
  );

  const payload = response?.data;

  return payload?.data ?? payload;
}

export async function holdBookingSessionOutstandingBalance(
  bookingSessionId: number
): Promise<any> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/payment-hold`
  );

  const payload = response?.data;

  return payload?.data ?? payload;
}

export type ChargeBookingSessionCustomAmountPayload = {
  amount: number;
  payment_type_id: number;
};

export async function chargeBookingSessionCustomAmount(
  bookingSessionId: number,
  payload: ChargeBookingSessionCustomAmountPayload
): Promise<any> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/charge-custom`,
    payload
  );

  const data = response?.data;

  return data?.data ?? data;
}

export type SendBookingSessionPaymentLinkPayload = {
  amount: number;
  payment_type_id: number;
  description?: string;
};

export async function sendBookingSessionPaymentLink(
  bookingSessionId: number,
  payload: SendBookingSessionPaymentLinkPayload
): Promise<any> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/payment-link`,
    payload
  );

  const data = response?.data;

  return data?.data ?? data;
}

export type RecordBookingSessionPaymentPayload = {
  amount: number;
  payment_type_id: number;
  source?: string;
  description?: string;
  external_reference?: string;
  idempotency_key?: string;
  paid_at?: string;
};

export type RecordBookingSessionPaymentSummary = {
  paid_amount: number;
  refunded_amount: number;
  outstanding_balance: number;
};

export type RecordBookingSessionPaymentResponse = {
  transaction?: BookingSession['payment_transaction'][number];
  summary?: RecordBookingSessionPaymentSummary;
  booking_session?: BookingSession;
};

export async function recordBookingSessionPayment(
  bookingSessionId: number,
  payload: RecordBookingSessionPaymentPayload
): Promise<RecordBookingSessionPaymentResponse> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/record-payment`,
    payload
  );

  const data = response?.data;

  return data?.data ?? data;
}

export type VoidBookingSessionPaymentPayload = {
  reason: string;
  amount?: number;
  description?: string;
  idempotency_key?: string;
};

export type VoidBookingSessionPaymentResponse = {
  transaction?: BookingSession['payment_transaction'][number];
  summary?: RecordBookingSessionPaymentSummary;
  booking_session?: BookingSession;
};

export async function voidBookingSessionPayment(
  bookingSessionId: number,
  transactionId: number,
  payload: VoidBookingSessionPaymentPayload
): Promise<VoidBookingSessionPaymentResponse> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/transactions/${transactionId}/void`,
    payload
  );

  const data = response?.data;

  return data?.data ?? data;
}

export type RefundBookingSessionPaymentPayload = {
  transaction_id: number;
  amount: number;
  reason: string;
  description?: string;
};

export type RepriceBookingSessionPayload = {
  price_per_hour: number;
  apply_to_all_future?: boolean;
  preview?: boolean;
  reason?: string;
};

export type RepriceBookingSessionPreviewItem = Record<string, unknown>;

export type RepriceBookingSessionResponse = {
  eligible?: RepriceBookingSessionPreviewItem[];
  eligible_sessions?: RepriceBookingSessionPreviewItem[];
  skipped?: RepriceBookingSessionPreviewItem[];
  skipped_sessions?: RepriceBookingSessionPreviewItem[];
  results?: RepriceBookingSessionPreviewItem[];
  updated_sessions?: BookingSession[];
  updatedSessions?: BookingSession[];
  booking_sessions?: BookingSession[];
  bookingSessions?: BookingSession[];
  booking_session?: BookingSession;
  bookingSession?: BookingSession;
  session?: BookingSession;
  updated_session?: BookingSession;
  updatedSession?: BookingSession;
  sessions?: BookingSession[];
  data?: unknown;
  message?: string;
};

export async function refundBookingSessionPayment(
  bookingSessionId: number,
  payload: RefundBookingSessionPaymentPayload
): Promise<any> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/refund`,
    payload
  );

  const data = response?.data;

  return data?.data ?? data;
}

export async function repriceBookingSession(
  bookingSessionId: number,
  payload: RepriceBookingSessionPayload
): Promise<RepriceBookingSessionResponse> {
  const response = await axios.post(
    `/bookings/sessions/${bookingSessionId}/reprice`,
    payload
  );

  const data = response?.data;

  return (data?.data ?? data) as RepriceBookingSessionResponse;
}

export type SendSessionPushNotificationPayload = {
  providerId: number;
  title: string;
  body: string;
  data?: Record<string, string | number | boolean | null | undefined>;
};

export async function sendSessionPushNotification(
  payload: SendSessionPushNotificationPayload
): Promise<any> {
  const response = await axios.post(
    `/notifications/provider/custom-message`,
    payload
  );
  const data = response?.data;
  return data?.data ?? data;
}
